import {
  Button,
  FieldWrapper,
  FlexBox,
  FormField,
  Icon,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { useState } from 'react';
import { useInterval } from 'react-use';

import { trustpilotApi } from 'apis/trustpilot/trustpilot.api';
import { TrustpilotIntegration } from 'apis/trustpilot/types/trustpilot.types';

import {
  ConnectionType,
  getConnectionStatusValues,
} from './utils/oAuthConnection';

type OAuthButtonProps = {
  integration: TrustpilotIntegration;
};

const pollDelayMs = 2000;
const maxFetchPolls = 30;

export const OAuthButton = ({ integration }: OAuthButtonProps): JSX.Element => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionType>(
    integration.connected ? 'CONNECTED' : 'NOT_CONNECTED',
  );
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [diagnosticError, setDiagnosticError] = useState<string>('');

  const [readTrustpilotIntegration] =
    trustpilotApi.useLazyReadTrustpilotIntegrationQuery();

  const { icon, color, tooltip } = getConnectionStatusValues(connectionStatus);
  const isConnecting = connectionStatus === 'CONNECTING';

  const resetPolling = (diagnosticMessage = '') => {
    setIsPolling(false);
    setPollCount(0);
    setDiagnosticError(diagnosticMessage);
  };

  const handleOAuthConnectCall = async () => {
    const windowWidth = 620;
    const windowHeight = 800;
    // centering the new window
    const left = window.innerWidth / 2 - windowWidth / 2;
    const top = window.innerHeight / 2 - windowHeight / 2;

    setConnectionStatus('CONNECTING');
    setIsPolling(true);
    window.open(
      integration.connect_url,
      'DixaTrustpilotReviewOAuth',
      `toolbar=no,
        location=no,
        status=no,
        menubar=no,
        scrollbars=yes,
        resizable=yes,
        width=${windowWidth},
        height=${windowHeight},
        left=${left},
        top=${top}`,
    );
  };

  const handleOAuthStatusCall = async () => {
    const response = await readTrustpilotIntegration(
      { id: integration.id },
      false,
    );
    if (isResponseWithData(response)) {
      const currentIntegration = response.data.data;
      if (currentIntegration.connected) {
        setConnectionStatus('CONNECTED');
        resetPolling();
      }

      /**
       * Diagnostic message should be different from the existing one (if
       * already there). For this purpose message also contains a timestamp in
       * case if error message itself is the same.
       */
      if (
        currentIntegration.connection_diagnostic_message &&
        currentIntegration.connection_diagnostic_message !==
          integration.connection_diagnostic_message
      ) {
        setConnectionStatus('CONNECTION_FAILED');
        resetPolling(currentIntegration.connection_diagnostic_message);
      }
    } else {
      setConnectionStatus('CONNECTION_FAILED');
      resetPolling();
    }
  };

  useInterval(
    () => {
      if (pollCount >= maxFetchPolls) {
        setConnectionStatus('CONNECTION_TIMEOUT');
        resetPolling();
      } else {
        handleOAuthStatusCall();
        setPollCount((count) => count + 1);
      }
    },
    isPolling ? pollDelayMs : null,
  );

  return (
    <FormField span={4}>
      <FieldWrapper
        label="Authorize with OAuth"
        description={
          connectionStatus === 'CONNECTION_FAILED' ? diagnosticError : ''
        }
      >
        <FlexBox flexDirection="row" alignItems="baseline" gap="300">
          <Button
            icon="key"
            variant="secondary"
            onClick={handleOAuthConnectCall}
            disabled={isConnecting}
          >
            Start authorization
          </Button>
          <Tooltip content={tooltip}>
            {isConnecting ? (
              <Spinner size={18} />
            ) : (
              <Icon name={icon} color={color} />
            )}
          </Tooltip>
        </FlexBox>
      </FieldWrapper>
    </FormField>
  );
};
